import React, { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { referenceApi } from '../../services/api';

export function formatHs(code) {
  const d = String(code || '').replace(/\D/g, '');
  if (d.length < 6) return d;
  return d.slice(0, 4) + '.' + d.slice(4, 6) + (d.length > 6 ? '.' + d.slice(6) : '');
}

// Searchable HS code input: type a code prefix or keywords; auto-suggests from the commodity text.
export function HsCodeField({ value, onChange, commodity, bulkCommodity, className = '', inputClassName = 'sv-input', testId = 'hs-code' }) {
  const [query, setQuery] = useState(value ? formatHs(value) : '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState('');
  const box = useRef(null);
  const lastSuggested = useRef('');

  useEffect(function () { if (value && formatHs(value) !== query) setQuery(formatHs(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(function () {
    const key = (bulkCommodity || '') + '|' + (commodity || '');
    if (value || key === lastSuggested.current || (!bulkCommodity && (commodity || '').trim().length < 4)) return;
    const t = setTimeout(async function () {
      try {
        const res = await referenceApi.suggestHs(bulkCommodity ? { bulk_commodity: bulkCommodity } : { commodity });
        lastSuggested.current = key;
        if (res.data.suggestion) { onChange(res.data.suggestion.code); setDesc(res.data.suggestion.description); }
      } catch (e) { /* suggestion is best-effort */ }
    }, 500);
    return function () { clearTimeout(t); };
  }, [commodity, bulkCommodity, value, onChange]);

  useEffect(function () {
    if (!open) return;
    const t = setTimeout(async function () {
      try { const res = await referenceApi.searchHs(query); setResults(res.data.results); } catch (e) { setResults([]); }
    }, 200);
    return function () { clearTimeout(t); };
  }, [query, open]);

  useEffect(function () {
    function away(e) { if (box.current && !box.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', away);
    return function () { document.removeEventListener('mousedown', away); };
  }, []);

  useEffect(function () {
    const digits = String(value || '').replace(/\D/g, '');
    if (digits.length >= 6 && !desc) {
      referenceApi.searchHs(digits.slice(0, 6)).then(function (r) { const hit = r.data.results.find(function (x) { return x.code === digits.slice(0, 6); }); if (hit) setDesc(hit.description); }).catch(function () {});
    }
  }, [value, desc]);

  function pick(entry) {
    onChange(entry.code); setQuery(formatHs(entry.code)); setDesc(entry.description); setOpen(false);
  }

  function typed(e) {
    const v = e.target.value;
    setQuery(v); setOpen(true); setDesc('');
    const digits = v.replace(/\D/g, '');
    if (/^[\d.\s]*$/.test(v) && digits.length >= 6 && digits.length <= 10) onChange(digits);
    else if (!v) onChange('');
  }

  const digits = String(value || '').replace(/\D/g, '');
  const invalid = query && !(digits.length >= 6 && digits.length <= 10);
  return (
    <div className={"relative " + className} ref={box}>
      <div className="relative">
        <input value={query} onChange={typed} onFocus={function () { setOpen(true); }} onBlur={function () { setTimeout(function () { setOpen(false); }, 150); }} placeholder="Search by keyword or type a code, e.g. 1701.99" className={inputClassName + ' pr-9 sv-mono'} data-testid={testId} autoComplete="off" />
        <Search className="h-4 w-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2" />
      </div>
      {desc && !open && <p className="text-xs text-gray-600 mt-1" data-testid={testId + '-description'}>{desc}</p>}
      {invalid && !open && <p className="text-xs text-error-700 mt-1">Enter a 6–10 digit HS code or pick one from the list</p>}
      {open && results.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full max-h-64 overflow-auto bg-white border border-gray-300 rounded-sm shadow-lg" data-testid={testId + '-results'}>
          {results.map(function (r) {
            return (
              <li key={r.code}>
                <button type="button" onMouseDown={function (e) { e.preventDefault(); pick(r); }} className="w-full text-left px-3 py-2 hover:bg-gray-50 flex gap-3 items-baseline" data-testid={testId + '-option-' + r.code}>
                  <span className="sv-mono text-sm text-ink shrink-0">{formatHs(r.code)}</span>
                  <span className="text-sm text-gray-700">{r.description}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
